import type {
  BoggleRunSummaryRatingId,
  BoggleRunSummaryTierCategoryId,
  BoggleRunSummaryTierLevel,
} from '~/boggle/config/runSummaryScore'
import type { BoggleBadgeId } from '~/boggle/config/badges/types'
import type { BogglePlaygroundSettings } from '~/boggle/config/playground'
import type { BogglePosition } from '~/boggle/engine/standard/types'
import type { BoggleSpecialTileKind } from '~/boggle/engine/specialTiles'
import type { BoggleGameModeId, BogglePublicRunKind } from '~/boggle/gameModes'
import type { BoggleEarnedBadge } from '~/boggle/lib/badges/evaluateRunBadges'

export const BOGGLE_COMPLETED_RUN_SCHEMA_VERSION = 5
export const BOGGLE_COMPLETED_RUN_FACTS_VERSION = 5
export const BOGGLE_RULES_VERSION = 1
export const BOGGLE_LEXICON_VERSION = 'boggle-standard-lexicon-v1'
export const BOGGLE_WORD_METADATA_VERSION = 1
export const BOGGLE_PLAYER_ACHIEVEMENT_STATE_VERSION = 1

export type BoggleCompletedRun = {
  completedAt: string
  /**
   * Historical badge awards shown to the player when the run ended.
   *
   * Schema-v1 completed runs predate this durable fact, so projection rebuilds
   * intentionally treat an absent array as "no recorded awards" rather than
   * re-evaluating old runs under today's badge definitions.
   */
  earnedBadges?: readonly BoggleEarnedBadge[]
  gameModeId: BoggleGameModeId
  id: string
  lexiconVersion: string
  ratingId: BoggleRunSummaryRatingId
  /**
   * Version of the neutral accepted-word/action fact contract below.
   *
   * Schema-v1/v2/v3 completed runs predate these timelines, so an absent value
   * means no durable neutral fact stream was recorded for that historical run.
   */
  runFactsVersion?: number
  runEnd?: BoggleCompletedRunEndFact
  rulesVersion: number
  runKind: BogglePublicRunKind
  schemaVersion: number
  setupSnapshot?: BoggleCompletedRunSetupSnapshot
  startedAt: string
  submittedWords: readonly string[]
  /**
   * Historical row-ready score facts shown when the run ended.
   *
   * Schema-v1/v2 runs predate Run History, so an absent snapshot means the
   * exact historical score and tier outcomes are no longer knowable. Consumers
   * should keep those runs visible and render an intentional legacy fallback
   * rather than re-evaluating old runs under today's mutable rules.
   */
  summarySnapshot?: BoggleCompletedRunSummarySnapshot
  /** Accepted-word facts preserved in acceptance order for future projections. */
  acceptedWordTimeline?: readonly BoggleCompletedRunAcceptedWordFact[]
  /** Semantic player actions preserved in gameplay order, not raw DOM noise. */
  actionTimeline?: readonly BoggleCompletedRunActionFact[]
  wordMetadataVersion: number
}

export type BoggleCompletedRunSummaryTierOutcome = {
  categoryId: BoggleRunSummaryTierCategoryId
  metricValue: number
  tierLevel: BoggleRunSummaryTierLevel | null
}

export type BoggleCompletedRunSummarySnapshot = {
  /**
   * Immutable historical totals added in schema v4. Schema-v3 snapshots only
   * preserved row facts, so these remain optional for old completed runs.
   */
  /** Player-facing gold credit, including physical gold tiles and Gold Rush virtual gold. */
  goldTilesPlayed?: number
  /** Physical board tiles that carried the gold special tag when accepted. */
  physicalGoldTilesPlayed?: number
  /** Effective special-tile credit per kind, including virtual gold from Gold Rush. */
  effectiveSpecialTileCounts?: Readonly<Record<BoggleSpecialTileKind, number>>
  /** Physical special-tile tags on board cells when accepted, by kind. */
  physicalSpecialTileCounts?: Readonly<Record<BoggleSpecialTileKind, number>>
  /** @deprecated Use physicalSpecialTileCounts. Kept for legacy snapshots. */
  specialTileCounts?: Readonly<Record<BoggleSpecialTileKind, number>>
  tierOutcomes: readonly BoggleCompletedRunSummaryTierOutcome[]
  total: number
  totalMeterEarned?: number
  totalOvermeterEarned?: number
  uncommonLettersPlayed?: number
  /** Gold credit supplied by Gold Rush on selected non-gold board tiles. */
  virtualGoldTilesPlayed?: number
}

export type BoggleCompletedRunPhase = 'gold-rush' | 'meter-burst' | 'overtime'

export type BoggleCompletedRunAcceptedWordPathStepFact = {
  effectiveSpecialKinds: readonly BoggleSpecialTileKind[]
  letter: string
  physicalSpecialKinds: readonly BoggleSpecialTileKind[]
  position: BogglePosition
  virtualSpecialKinds: readonly BoggleSpecialTileKind[]
  /** @deprecated runFactsVersion < 4 */
  effectiveGoldSource?: 'gold-rush' | 'tile' | null
  /** @deprecated runFactsVersion < 4 */
  specialKinds?: readonly BoggleSpecialTileKind[]
}

export type BoggleCompletedRunSetupBoardCell = {
  id: number
  letter: string
  tags?: readonly string[]
}

export type BoggleCompletedRunChallengeSetupSnapshot = {
  aceScore: number
  boardDistribution: unknown
  bonuses: unknown
  configurationId: string | null
  cores: unknown
  gameplay: unknown
  pathMode: string
  progressSeed?: number | string
  specialTileReveals: unknown
  targetScore: number
}

export type BoggleCompletedRunSetupSnapshot = {
  boardCols: number
  boardRows: number
  challenge?: BoggleCompletedRunChallengeSetupSnapshot | null
  gameModeCorePreset: BogglePlaygroundSettings
  openingBoard: ReadonlyArray<ReadonlyArray<BoggleCompletedRunSetupBoardCell | null>>
  resolvedPlaygroundSettings: BogglePlaygroundSettings
  seed: number
  seedInput: number | string
}

export type BoggleCompletedRunEndFact = {
  acceptedWordCount: number
  endedAtMs: number
  endReason: 'no-words' | 'timeout'
  ledgerIndex: number
  /** @deprecated runFactsVersion < 4 — duplicate of acceptedWordCount */
  submittedWordCount?: number
  sumAcceptedWordLengths: number
}

export type BoggleCompletedRunAcceptedWordFact = {
  acceptedDuringOvertime: boolean
  acceptedAtMs: number
  boardCols: number
  boardRows: number
  clearedPositions: ReadonlyArray<BogglePosition>
  duringMeterBurstWindow: boolean
  effectiveSpecialTileCountsUsed?: Readonly<Record<BoggleSpecialTileKind, number>>
  burstActiveAtAcceptance: boolean
  /** Original run-ledger index; absent only on older runFactsVersion records. */
  ledgerIndex?: number
  meterGain: number
  meterBurstActiveAfter: boolean
  /** @deprecated runFactsVersion < 5 — use `duringMeterBurstWindow`. */
  duringTimerEndGoldRushWindow?: boolean
  /** @deprecated runFactsVersion < 5 — use `burstActiveAtAcceptance`. */
  goldRushActiveAtAcceptance?: boolean
  /** @deprecated runFactsVersion < 5 — use `meterBurstActiveAfter`. */
  meterGoldRushActiveAfter?: boolean
  overtimeSurvivingSelectionCount: number
  overmeterGain: number
  path: ReadonlyArray<BogglePosition>
  pathSteps?: readonly BoggleCompletedRunAcceptedWordPathStepFact[]
  physicalSpecialTileCountsUsed?: Readonly<Record<BoggleSpecialTileKind, number>>
  tileLetters: readonly string[]
  uncommonLetterCount: number
  uniqueTilesCleared: number
  word: string
  /** @deprecated runFactsVersion < 4 */
  effectiveGoldTilesUsed?: number
  /** @deprecated runFactsVersion < 4 */
  physicalGoldTilesUsed?: number
  /** @deprecated runFactsVersion < 4 */
  specialTileCountsUsed?: Readonly<Record<BoggleSpecialTileKind, number>>
  /** @deprecated runFactsVersion < 4 */
  virtualGoldTilesUsed?: number
}

export type BoggleCompletedRunActionFact =
  | {
      actionKind: 'selected-letter'
      atMs: number
      letter: string
      /** Original run-ledger index; absent only on older runFactsVersion records. */
      ledgerIndex?: number
      position: BogglePosition
    }
  | {
      actionKind: 'backspace' | 'clear'
      atMs: number
      /** Original run-ledger index; absent only on older runFactsVersion records. */
      ledgerIndex?: number
    }
  | {
      actionKind: 'submit'
      atMs: number
      /** Original run-ledger index; absent only on older runFactsVersion records. */
      ledgerIndex?: number
      word: string
    }
  | {
      actionKind: 'phase-start'
      atMs: number
      /** Original run-ledger index; absent only on older runFactsVersion records. */
      ledgerIndex?: number
      phase: BoggleCompletedRunPhase
    }

export type BoggleWordLexicalTag =
  | 'alphabetical'
  | 'anadrome'
  | 'double-letter'
  | 'isogram'
  | 'palindrome'
  | 'straight'

export type BoggleWordOccurrence = {
  displayWord: string
  gameModeId: BoggleGameModeId
  id: string
  lexiconVersion: string
  normalizedWord: string
  playedAt: string
  ratingId: BoggleRunSummaryRatingId
  runId: string
  runKind: BogglePublicRunKind
  submittedWordIndex: number
  wordMetadataVersion: number
}

export type BoggleWordModeCounts = Partial<
  Record<BoggleGameModeId, Partial<Record<BogglePublicRunKind, number>>>
>

export type BoggleWordStat = {
  displayWord: string
  firstPlayedAt: string
  highestRatingId: BoggleRunSummaryRatingId
  lastPlayedAt: string
  length: number
  lexicalTags: readonly BoggleWordLexicalTag[]
  lexiconVersion: string
  normalizedWord: string
  perModeCounts: BoggleWordModeCounts
  timesPlayed: number
  wordMetadataVersion: number
}

export type BoggleWordProjectionSnapshot = {
  wordOccurrences: BoggleWordOccurrence[]
  wordStats: BoggleWordStat[]
}

export type PlayerAchievementState = {
  /** Tiers earned by live evaluation or historical backfill. */
  unlockedTierIds: readonly string[]
  /**
   * Tiers unlocked by historical rebuild/backfill. Presentation can exclude
   * these from "new this run" choreography.
   */
  backfilledTierIds: readonly string[]
  /** Hidden rating ids that are safe to mention in achievement UI. */
  revealedRatingIds: readonly BoggleRunSummaryRatingId[]
  schemaVersion: number
}

export type BoggleBadgeProgressRecord = {
  countsByRating: Partial<Record<BoggleRunSummaryRatingId, number>>
  highestRating: BoggleRunSummaryRatingId | null
}

/**
 * Rebuildable Compendium-shaped projection derived from durable completed-run
 * facts. Compendium reads this shape directly; the materialized copy remains
 * disposable because completed runs are the authority.
 */
export type BoggleBadgeProgressProjection = {
  perBadge: Partial<Record<BoggleBadgeId, BoggleBadgeProgressRecord>>
  profileRunsByRating: Partial<Record<BoggleRunSummaryRatingId, number>>
}

export type BoggleUnviewedBadgeKind = 'improved' | 'new'

/** Durable until the badge list is opened; rows are deleted, not flagged viewed. */
export type BoggleUnviewedBadge = {
  badgeId: BoggleBadgeId
  kind: BoggleUnviewedBadgeKind
  /** Run rating when this new/improved state was earned (filter visibility). */
  ratingId: BoggleRunSummaryRatingId
}

/** Durable until the achievements list is opened; rows are deleted, not flagged viewed. */
export type BoggleUnviewedAchievementTier = {
  tierId: string
}

/** Durable until the lexicon is opened; rows are deleted, not flagged viewed. */
export type BoggleUnviewedWord = {
  normalizedWord: string
}

